import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import moment from 'moment';
import {
  cleanStoredArray,
  cleanStoredObject,
  getKey,
  getKeys,
  setKey,
  addToStoredArray,
  getEstimateJobs,
  getAllJobs,
  storeFile,
  cleanStoredDocumentsAndFiles,
} from '../utilities/Storage';
import {
  addNewExpense,
  addSupplierLocation,
  addNoteToJob,
  createJob,
  getFileData,
  jobStarted,
  jobPaused,
  jobCannotDo,
  jobCannotStart,
  jobCompleted,
  jobEstimatesProvided,
  storeEstimateJobs,
  storeJobsById,
  storePlans,
  storeProperties,
  storeSelfEmployedProperties,
  storeSettings,
  storeSubStatuses,
  updateExpense,
  updateChecklist,
  submitCosts,
} from '../utilities/ApiTasks';
import {
  ammendUpdate,
  getUpdates,
  removeUpdate,
  addProcessedUpdate,
  addFailedUpdate,
  addToLog,
} from '../utilities/Updates';
import { Geolocation } from '@capacitor/geolocation';
import { App } from '@capacitor/app';
import { InternetConnectionStatusContext } from './InternetConnectionStatusContext';
import { userIsSelfEmployed } from '../dataStructures/user/predicates';
import {IonToast} from "@ionic/react";
import {UserFriendlyError} from "../utilities/UserFriendlyError"
import { useAuthentication } from '../authentication/authentication-hook';

export const ApiContext = React.createContext('');

export default function ApiContainer({ children }) {
  const { user, isLoggedIn } = useAuthentication();
  const { hasInternetConnection, internetConnectionSet } = useContext(InternetConnectionStatusContext);
  const [estimateJobs, setEstimateJobs] = useState(0);
  const [planUpdated, setPlanUpdated] = useState();
  const [dataInitialised, setDataInitialised] = useState(false);
  const [selfEmployedJobsCount, setSelfEmployedJobsCount] = useState(false);
  const [stackProcessing, setStackProcessing] = useState(false);
  const [toast, setToast] = useState();
  const [toastColor, setToastColor] = useState('success');
  const [unsyncedCount, setUnsyncedCount] = useState(0);

  const doingWork = useRef(false);
  const isLoggedInRef = useRef(isLoggedIn);
  const internetRef = useRef(hasInternetConnection);
  const userRef = useRef(user);

  const refreshUnsyncedCount = async () => {
    try {
      const pending = await getUpdates();
      const pendingCount = Array.isArray(pending) ? pending.length : 0;

      const failed = await getKey('failedUpdates', []);
      const failedCount = Array.isArray(failed) ? failed.length : 0;

      const total = pendingCount + failedCount;
      setUnsyncedCount(total);
      return total;
    } catch (err) {
      return 0;
    }
  };

  const doWork = async () => {
    if (doingWork.current) {
      const dws = await getKey('doingWorkStarted', '1899-01-01 00:00:00');
      const doingWorkStarted = moment(dws);

      const tenMinutesAgo = moment().subtract(10, 'minutes');
      // oh oh doing work for more than ten minutes - something has gone wrong
      if (doingWorkStarted < tenMinutesAgo) {
        doingWork.current = false;
      }
      return;
    }
    setKey('doingWorkStarted', moment().format('YYYY-MM-DD HH:mm:ss'));

    doingWork.current = true;

    // process stack on every interval
    await processStack();

    const updatedCommon = await updateCommon();
    await updateLocation();
    await doCleanup();

    // lowest priority - get files from jobs
    if (updatedCommon) {
      await getFilesFromJob();
    }

    //only set this to false when its actualy finished doing work
    doingWork.current = false;
  };

  const processStack = async () => {
    const stack = await getUpdates();

    if (stack.length === 0) {
      return null;
    }
    // refresh unsynced count immediately (so UI shows pending items quickly)
    setStackProcessing(true);

    let result = undefined;
    let successCnt = 0;
    for (let update of stack) {
      const { data } = update.value;

      addToLog('update', update);

      try {
        switch (update.value.action) {
          case 'add_new_expense':
            result = await addNewExpense(data.jobId, data.obj);
            break;
          case 'update_expense':
            result = await updateExpense(
                data.jobId,
                data.expenseId,
                data.obj
            );
            break;
          case 'job_started':
            result = await jobStarted(data.jobId, data.fromdatetime);
            break;
          case 'job_paused':
            result = await jobPaused(data.jobId, data.fromdatetime);
            break;
          case 'job_completed':
            result = await jobCompleted(data.jobId, data.fromdatetime);
            break;
          case 'job_cannotdo':
          case 'job_cannotcomplete':
            result = await jobCannotDo(data.jobId, data.noteText, data.noteSubject, data.fromdatetime);
            break;
          case 'job_cannotstart':
            result = await jobCannotStart(data.jobId, data.noteText, data.noteSubject, data.fromdatetime);
            break;
          case 'job_estimates_provided':
            result = await jobEstimatesProvided(data.jobId);
            break;
          case 'add_note_to_job':
            result = await addNoteToJob(
                data.jobId,
                data.noteText,
                data.photos,
                data.documents,
            );
            break;
          case 'update_checklist':
            result = await updateChecklist(
                data.jobId,
                data.workOrderPmsChecklistId,
                data.obj
            );
            break;
          case 'create_job':
            result = await createJob(data.obj);

            setPlanUpdated(moment());
            break;
          case 'submit_costs':
            try {
              result = await submitCosts(data.jobId, data.invoicedate);
              await storeJobsById(data.jobId); //refresh the job status
            } catch (err) {
              if (err instanceof UserFriendlyError) {
                setToastColor('warning');
                setToast(err.message);

                result = true;
              } else {
                throw err;
              }
            }
            break;
        }
      } catch (err) {
        result = false;
      }

      if (result) {
        addToLog('update_success', update);

        await removeUpdate(update.ref);
        await addProcessedUpdate(update);
        successCnt++;
      } else {
        addToLog('update_failure', update);

        if (update.value.retryCount >= 5) {
          addToLog('update_perm_failure', update);

          // update has failed 5 times in a row, so move it to permanent failures
          await removeUpdate(update.ref);
          await addFailedUpdate(update);

        } else {
          ammendUpdate(update.ref, {...update.value, retryCount: update.value.retryCount + 1});
          // exit processStack() early in case the update failed because of lack of connection,
          // so we don't continue processing more updates
          setStackProcessing(false);
          return false;
        }
      }

      setStackProcessing(false);
    }

    setStackProcessing(false);
    // refresh the badge count (pending + permanently failed)
    await refreshUnsyncedCount();

    if (stack.length > 0) {
      if(successCnt === stack.length) {
        return true;
      } else {
        return false;
      }
    }

    return null;
  }

  const updateCommon = async () => {
    const lcu = await getKey('lastCommonUpdate', '1899-01-01 00:00:00');
    const lastCommonUpdate = moment(lcu);
    const oneHourAgo = moment().subtract(1, 'hours');

    // update common stuff every hour
    if (lcu === "" || lastCommonUpdate < oneHourAgo) {
      addToStoredArray('debug', 'updating common');
      let advanceDays = 0;
      if (lcu === '1899-01-01 00:00:00') {
        // don't get current day's data because this will be retrieved by Jobs.js
        advanceDays = 1;
      }

      if (!await storeSubStatuses()) { return; };
      if (userRef.current && !userIsSelfEmployed(userRef.current)) {

        if (!await storePlans(moment().add(advanceDays, 'days').format('YYYY-MM-DD'), 5)) { return; };
        if (!await storeEstimateJobs()) { return; };
        if (!await storeSettings()) { return; }
        if (!await storeProperties()) { return; }

        setPlanUpdated(moment());

        const ej = await getEstimateJobs();
        setEstimateJobs(ej.length);
      }

      setDataInitialised(true);
      setKey('lastCommonUpdate', moment().format('YYYY-MM-DD HH:mm:ss'));
    }
  }

  const updateLocation = async () => {
    const llu = await getKey('lastLocationUpdate', '1899-01-01 00:00:00');
    const lastLocationUpdate = moment(llu);

    const fiveMinutesAgo = moment().subtract(5, 'minutes');

    // update location every five mins
    if (lastLocationUpdate < fiveMinutesAgo) {
      await setKey('lastLocationUpdate', moment().format('YYYY-MM-DD HH:mm:ss'));

      try {
        const position = await Geolocation.getCurrentPosition();

        await addSupplierLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          loggeddatetime: moment().format('YYYY-MM-DD HH:mm:ss'),
        });
      } catch(error) {
        //error getting location try again on next pass through
        await setKey('lastLocationUpdate', moment(fiveMinutesAgo).format('YYYY-MM-DD HH:mm:ss'));
      }
    }
  }

  const doCleanup = async () => {
    const ldc = await getKey('lastDatabaseCleanup', '1899-01-01 00:00:00');
    const lastDatabaseCleanup = moment(ldc);

    const oneDayAgo = moment().subtract(1, 'days');

    // clean the database every day (delete old shit)
    if (lastDatabaseCleanup < oneDayAgo) {
      await setKey('lastDatabaseCleanup', moment().format('YYYY-MM-DD HH:mm:ss'));

      const weeks = 2;
      await cleanStoredObject('estimatesSubmitted', weeks);
      await cleanStoredObject('jobFlags', weeks);
      await cleanStoredObject('jobs', weeks);
      await cleanStoredObject('plans', weeks);
      await cleanStoredObject('properties', weeks);
      await cleanStoredObject('submittedChecklists', weeks);
      await cleanStoredObject('submittedNotes', weeks);

      await cleanStoredArray('failedUpdates', weeks);
      await cleanStoredArray('processedUpdates', weeks);
      await cleanStoredArray('log', weeks);

      await cleanStoredDocumentsAndFiles(weeks);
    }
  }

  const getFilesFromJob = async () => {
    const jobs = await getAllJobs();
    const keys = await getKeys();
    const now = moment().startOf('day').subtract(10, 'days');

    for (const val of Object.values(jobs)) {
      const job = val.value;
      if (!job.preferredstartdatetime) { continue; }

      if (moment(job.preferredstartdatetime) >= now) {

        for (const document of job.documents) {
          let file = undefined;

          if (document.image) { file = document.image.file; }
          if (document.document) { file = document.document.file; }

          if (file) {
            const fileId = file.substr(file.lastIndexOf("/") + 1);
            const find = keys.find(key => key.startsWith(`file_${fileId}_`));

            if (!find) {
              const data = await getFileData(fileId);
              await storeFile(fileId, data);
            }
          }
        };
      }
    };
  }

  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
    internetRef.current = hasInternetConnection;
    userRef.current = user;
  }, [isLoggedIn, hasInternetConnection, user]);

  const MIN_AWAY_MS = 30000;
  const MIN_RESYNC_GAP_MS = 10000;

  const lastSyncRef = useRef(0);
  const inFlightRef = useRef(false);
  const wentInactiveAtRef = useRef(null);
  const prevConnectionRef = useRef(hasInternetConnection);
  const doWorkRef = useRef(doWork);

  useEffect(() => {
    doWorkRef.current = doWork;
  });

  const triggerResync = useCallback(async (reason) => {
    if (!isLoggedInRef.current || !userRef.current || !internetRef.current) return;
    if (inFlightRef.current) return;
    if (Date.now() - lastSyncRef.current < MIN_RESYNC_GAP_MS) return;

    inFlightRef.current = true;
    try {
      console.log(`${reason} - triggering doWork`);
      await doWorkRef.current();
      lastSyncRef.current = Date.now();
    } catch (error) {
      console.warn(`doWork failed after ${reason}`, error);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    const wasOffline = prevConnectionRef.current === false;
    prevConnectionRef.current = hasInternetConnection;

    if (hasInternetConnection && wasOffline) {
      triggerResync('connection restored');
    }
  }, [hasInternetConnection, triggerResync]);

  useEffect(() => {
    const handle = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        if (wentInactiveAtRef.current === null) {
          wentInactiveAtRef.current = Date.now();
        }
        return;
      }

      const awayMs = wentInactiveAtRef.current
          ? Date.now() - wentInactiveAtRef.current
          : 0;
      wentInactiveAtRef.current = null;

      if (awayMs < MIN_AWAY_MS) return;
      triggerResync(`app resumed after ${Math.round(awayMs / 1000)}s`);
    });

    return () => {
      handle.then((listener) => listener.remove());
    };
  }, [triggerResync]);

  useEffect(() => {
    // Reset lastCommonUpdate to force data to refresh on app load
    setKey('lastCommonUpdate', '1899-01-01 00:00:00');
    setKey('lastLocationUpdate', '1899-01-01 00:00:00');

    setDataInitialised(false);

    // refresh unsynced count on startup
    refreshUnsyncedCount();

    if (!internetConnectionSet) return;

    const run = () => {
      // keep the unsynced count fresh regardless of connectivity/work state,
      // so the banner shows as soon as updates are stored (even while offline)
      refreshUnsyncedCount();

      if (!isLoggedInRef.current || !internetRef.current || !userRef.current) { return;}
      doWork();
    };

    run();

    const intervalId = setInterval(run, 5000);

    return () => clearInterval(intervalId);
  }, [internetConnectionSet, isLoggedIn]);

  const resubmitUpdates = async () => {
    if (!internetRef.current) {
      setToastColor('warning');
      setToast('No internet connection. Your updates are saved locally and will be retried when you reconnect.');
      return;
    }

    if (doingWork.current) {
      setToastColor('warning');
      setToast('Sync already in progress...');
      return;
    }

    try {
      doingWork.current = true;
      const result = await processStack();

      if (result === true) {
        setToastColor('success');
        setToast('Your updates have now synced successfully.');
      } else if (result === false) {
        setToastColor('warning');
        setToast('Some updates could not be synced. They are saved locally for you to retry later.');
      }
    } catch (err) {
      setToastColor('danger');
      setToast('Resubmit failed. Your updates are saved locally and will be retried later.');
    } finally {
      doingWork.current = false;
      setStackProcessing(false);
    }
  };

  return (
      <ApiContext.Provider value={{
        estimateJobs,
        planUpdated,
        dataInitialised,
        stackProcessing,
        setSelfEmployedJobsCount,
        selfEmployedJobsCount,
        unsyncedCount,
        resubmitUpdates,
      }}>
        {children}
        <IonToast
            isOpen={toast && toast.length > 0}
            onDidDismiss={() => {setToast(''); setToastColor('success');}}
            message={toast}
            duration={5000}
            color={toastColor}
            position="middle"
        />
      </ApiContext.Provider>
  );
}